/**
 * B-24b WS-3 — SA Operator Console KPI strip.
 *
 * 4 cells wired to real data from useSaKpis():
 *   1. Active customers (+ delta vs prior week; null delta = omitted)
 *   2. ARR (from tenant_contract; null delta = omitted)
 *   3. AI approval rate (cross-tenant up/total over ai_ratings; 0.0–1.0 ratio
 *      from the backend → multiplied ×100 for "92%" display; total=0 →
 *      "building baseline" placeholder)
 *   4. Customers needing attention (RED on nonzero — the "calm until it isn't"
 *      cell; attention=0 → a calm "All clear" state badge, never a faux-delta)
 *
 * Numerals (P0-1): large bold mono "0"/"$0" use `.num-kpi` (zero feature off)
 * so a "0" never reads as an Ø error glyph.
 *
 * Low-data rules (IV-L1, IV-L2):
 *   - delta: null → NO badge rendered (never "+0%")
 *   - approval rate value=null → "building baseline" label
 *   - ARR with value 0 and no contract → "$0"
 */
import { useSaKpis } from "@/hooks/queries/admin/useSaDashboard";

function fmtArr(cents: number): string {
  if (cents === 0) return "$0";
  const dollars = cents / 100;
  if (dollars >= 1_000_000) return `$${(dollars / 1_000_000).toFixed(1)}M`;
  if (dollars >= 1_000) return `$${(dollars / 1_000).toFixed(0)}k`;
  return `$${dollars.toLocaleString()}`;
}

function fmtArrDelta(delta: number | null): string | null {
  if (delta === null) return null;
  const d = delta / 100;
  if (d === 0) return null; // suppressed per IV-L2
  return d > 0 ? `+$${Math.round(d).toLocaleString()}` : `-$${Math.abs(Math.round(d)).toLocaleString()}`;
}

interface CellProps {
  testId: string;
  label: string;
  value: string;
  delta?: string | null;
  deltaPositive?: boolean;
  isAlert?: boolean;
  loading?: boolean;
  placeholder?: boolean;
  placeholderLabel?: string;
  /** Calm non-numeric state badge (e.g. "All clear") — distinct from a delta. */
  stateBadge?: { label: string; tone: "calm" | "alert" } | null;
}

function Cell({
  testId,
  label,
  value,
  delta,
  deltaPositive,
  isAlert = false,
  loading = false,
  placeholder = false,
  placeholderLabel,
  stateBadge,
}: CellProps) {
  return (
    <div data-testid={testId} className="bg-surface-card px-4 py-3.5">
      <div className="text-2xs font-bold uppercase tracking-[0.12em] text-text-tertiary">
        {label}
      </div>
      {loading ? (
        <div className="mt-2.5 h-7 w-20 animate-pulse rounded bg-surface-canvas" />
      ) : placeholder ? (
        <div className="mt-2">
          <span className="font-mono text-sm tabular-nums text-text-tertiary italic">
            {placeholderLabel ?? "—"}
          </span>
        </div>
      ) : (
        <div className="mt-2 flex items-baseline gap-2">
          <span
            className={
              "num-kpi font-mono text-2xl font-bold tracking-tight " +
              (isAlert ? "text-brand-negative" : "text-text-emphasis")
            }
          >
            {value}
          </span>
          {stateBadge ? (
            <span className="flex items-center gap-1.5">
              <span
                className={
                  "h-1.5 w-1.5 rounded-full " +
                  (stateBadge.tone === "calm"
                    ? "bg-brand-positive"
                    : "bg-brand-negative")
                }
                aria-hidden="true"
              />
              <span
                className={
                  "text-2xs font-medium " +
                  (stateBadge.tone === "calm"
                    ? "text-brand-positive"
                    : "text-brand-negative")
                }
              >
                {stateBadge.label}
              </span>
            </span>
          ) : (
            delta != null && (
              <span
                className={
                  "num-kpi font-mono text-2xs font-medium " +
                  (isAlert
                    ? "text-brand-negative"
                    : deltaPositive === undefined
                      ? "text-text-tertiary"
                      : deltaPositive
                        ? "text-brand-positive"
                        : "text-brand-negative")
                }
              >
                {delta}
              </span>
            )
          )}
        </div>
      )}
    </div>
  );
}

export function OperatorKpiStrip() {
  const { data, isLoading } = useSaKpis();

  const customerDelta: string | null = (() => {
    if (!data) return null;
    const d = data.active_customers.delta;
    if (d === null || d === 0) return null;
    return d > 0 ? `+${d} wk` : `${d} wk`;
  })();

  const arrDelta = data ? fmtArrDelta(data.arr_cents.delta) : null;

  // AI approval rate — backend value is a 0.0–1.0 ratio → ×100 for "92%"
  // display (P0-1 / B1). value=null (no ratings yet) → "building baseline".
  const approvalPlaceholder = !isLoading && !!data && data.bias_pass_rate.value == null;
  const approvalValue =
    data?.bias_pass_rate.value != null
      ? `${Math.round(data.bias_pass_rate.value * 100)}%`
      : "—";
  const approvalDelta: string | null = (() => {
    if (!data || data.bias_pass_rate.delta === null) return null;
    const d = data.bias_pass_rate.delta * 100;
    if (d === 0) return null;
    return d > 0 ? `+${d.toFixed(1)}pt` : `${d.toFixed(1)}pt`;
  })();

  const attentionCount = data?.needs_attention.value ?? 0;
  const isAttentionAlert = attentionCount > 0;

  return (
    <div
      data-testid="operator-kpi-strip"
      className="grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-border-default bg-border-default lg:grid-cols-4"
    >
      <Cell
        testId="kpi-active-customers"
        label="Active customers"
        value={data ? String(data.active_customers.value) : "—"}
        delta={customerDelta}
        deltaPositive={customerDelta !== null ? customerDelta.startsWith("+") : undefined}
        loading={isLoading}
      />
      <Cell
        testId="kpi-arr"
        label="ARR"
        value={data ? fmtArr(data.arr_cents.value) : "—"}
        delta={arrDelta}
        deltaPositive={arrDelta !== null ? arrDelta.startsWith("+") : undefined}
        loading={isLoading}
      />
      <Cell
        testId="kpi-bias-pass-rate"
        label="AI approval rate"
        value={approvalValue}
        delta={approvalDelta}
        deltaPositive={
          approvalDelta !== null ? approvalDelta.startsWith("+") : undefined
        }
        loading={isLoading}
        placeholder={approvalPlaceholder}
        placeholderLabel="building baseline"
      />
      <Cell
        testId="kpi-needs-attention"
        label="Needs attention"
        value={String(attentionCount)}
        isAlert={isAttentionAlert}
        loading={isLoading}
        stateBadge={
          isAttentionAlert
            ? { label: "review now", tone: "alert" }
            : { label: "All clear", tone: "calm" }
        }
      />
    </div>
  );
}
